import React, { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { format } from 'date-fns'

import { Stack, Box, Typography, Tooltip } from '@mui/material'
import { CopyIcon, Trash2Icon, PencilIcon, XIcon, CheckIcon, UsersIcon, UserPlusIcon, UserMinusIcon } from 'lucide-react'

import {
  useRolesMatrix,
  useRenameCompanyRole,
  useRoleMembers,
  useRemoveRoleMember,
  MatrixRoleItem,
} from 'hooks/api'
import { useCanAccess, useReferrerState } from 'hooks'
import { Button, Card, Input } from '@repo/ui'
import {
  Header,
  EmptyState,
  PermissionsList,
  Table,
  UserCell,
  StatusChipCell,
  ActionDropdown,
  DeleteRoleModal,
  DuplicateRoleModal,
  AssignUsersModal,
} from 'components'
import { getRoleDisplayName, getRoleStatus } from 'constants/roles'
import { COMPANY_USER_STATUSES, ROLE_STATUSES } from 'constants/statuses'
import { ROUTES } from 'routes'
import { RolePageSkeleton, ROLE_MEMBER_COLUMNS } from './RolePageSkeleton'
import { vhMinus } from 'utils/textZoom'

// ── Component ─────────────────────────────────────────────────────────────────

export const RolePage = () => {
  const { name = '' } = useParams()
  const navigate = useNavigate()
  const { data, isLoading, isFetching, refetch } = useRolesMatrix()
  // Detail → detail: the derived label would be the Roles list, not this role.
  const referrer = useReferrerState('Role')

  // Details card edit state (inline rename — only for custom roles)
  const [isEditingName, setIsEditingName] = useState(false)
  const [nameValue, setNameValue] = useState('')

  const renameRole = useRenameCompanyRole()

  // Modal state
  const [openDeleteModal, setOpenDeleteModal] = useState(false)
  const [openDuplicateModal, setOpenDuplicateModal] = useState(false)
  const [openAssignModal, setOpenAssignModal] = useState(false)

  const role = data?.roles?.find((r: MatrixRoleItem) => r.name === name)

  // The roleId the membership endpoints expect, resolved defensively so the
  // hooks below can run on EVERY render (Rules of Hooks) — including the loading
  // render (no data yet) and the brief render after a create/rename where `role`
  // isn't in the freshly-refetched matrix yet. Empty string → useRoleMembers is
  // `enabled: false`, the remove mutation never fires until used.
  //   built-in (including overridden/Modified): builtinRoleId → global roles table
  //   standalone custom: role.id → company_custom_roles join table
  const memberRoleId = role ? (role.builtinRoleId ?? role.id) : ''

  // ── Membership + permission gate ─────────────────────────────────────────────
  // Declared BEFORE every early return (isLoading / !role) so the hook order
  // stays stable across renders. Previously these sat after the returns with
  // eslint-disable hacks, which crashed with "Rendered more hooks" the moment a
  // loading/not-found render was followed by a loaded/found one.
  const { data: members = [], isLoading: isMembersLoading, refetch: refetchMembers } = useRoleMembers(memberRoleId)
  const removeRoleMember = useRemoveRoleMember(memberRoleId)
  const { isHasAccess: canManageMembers } = useCanAccess({ action: 'assign_users' })

  // Footprint-parity placeholder (§19) in place of the centred <Loader />, which reserved none of
  // the 118px header or the viewport-filling two-column body. See RolePageSkeleton.
  if (isLoading) return <RolePageSkeleton />

  if (!role) {
    // A freshly created/renamed role may not be in the cached matrix yet while
    // the post-mutation refetch is in flight. Show the loader rather than
    // flashing "Role not found" until that fetch settles.
    if (isFetching) return <RolePageSkeleton />
    return (
      <EmptyState
        title="Role not found"
        text="This role does not exist or you do not have permission to view it."
      />
    )
  }

  const displayName = getRoleDisplayName(role)

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleEditName = () => {
    setNameValue(displayName)
    setIsEditingName(true)
  }

  const handleCancelEdit = () => {
    setIsEditingName(false)
    setNameValue('')
  }

  const handleSaveName = async () => {
    const trimmed = nameValue.trim()
    if (!trimmed || trimmed === displayName) {
      handleCancelEdit()
      return
    }
    await renameRole.mutateAsync({ id: role.id, name: trimmed })
    setIsEditingName(false)
    refetch()
    // Navigate to the new slug so the URL stays in sync
    navigate(ROUTES.SETTINGS_COMPANY_ROLE(trimmed), { replace: true })
  }

  const handleDeleted = () => {
    navigate(ROUTES.SETTINGS_COMPANY_ROLES)
  }

  // ── Members table columns ────────────────────────────────────────────────────
  // Plain const (not useMemo) so it can live after the early returns above without
  // a conditional-hook violation; cheap to rebuild per render.
  // Geometry (field/header/width/pinning) comes from ROLE_MEMBER_COLUMNS, shared with the loading
  // skeleton so the placeholder table and the real one cannot disagree about column widths.
  const memberColumns = [
    {
      ...ROLE_MEMBER_COLUMNS[0],
      renderCell: ({ row }) => (
        // Normalize camelCase IRoleMember fields to the snake_case shape
        // that UserBlock/UserCell expects (first_name, last_name, id).
        <UserCell
          user={{
            id: row.userId,
            email: row.email,
            first_name: row.firstName,
            last_name: row.lastName,
          }}
          route={ROUTES.SETTINGS_COMPANY_USER(row.userId)}
        />
      ),
    },
    {
      ...ROLE_MEMBER_COLUMNS[1],
      renderCell: ({ row }) => (
        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {row.email}
        </Typography>
      ),
    },
    {
      ...ROLE_MEMBER_COLUMNS[2],
      renderCell: ({ row }) => {
        const items: any[] = [
          {
            label: 'View user',
            icon: <UsersIcon size={16} strokeWidth={1} />,
            onClick: () => navigate(ROUTES.SETTINGS_COMPANY_USER(row.userId), { state: referrer }),
          },
        ]
        // §27: an in-context action stays VISIBLE and goes disabled with a tooltip naming
        // the missing permission, rather than vanishing — hiding it left no path to
        // discover or request access. Applies to both kebab rows in this file and the
        // header's primary "Assign users" button below.
        items.push({
          label: 'Remove from role',
          icon: <UserMinusIcon size={16} strokeWidth={1} />,
          destructive: true,
          disabled: !canManageMembers,
          tooltip: canManageMembers
            ? undefined
            : "You need the 'Assign users' permission. Ask an admin to grant it.",
          onClick: () => removeRoleMember.mutate(row.userId, {
            onSuccess: () => refetchMembers(),
          }),
        })
        return <ActionDropdown items={items} />
      },
    },
  ]

  // ── Render ───────────────────────────────────────────────────────────────────

  // Build a CompanyRoleListItem-shaped object for the existing modals
  const roleForModals = {
    id: role.id,
    name: role.name,
    isCustom: role.isCustom,
    isOverridden: role.isOverridden,
    builtinRoleId: role.builtinRoleId,
  }

  // Ids of current members — passed to AssignUsersModal to exclude them
  const existingMemberIds = members.map((m) => m.userId)

  return (
    /*
      DELIBERATELY NOT capped to NARROW_COLUMN_WIDTH, unlike its siblings InvoicePage and
      UserPage. Recorded here because "cap the standalone settings detail pages" is an easy
      sweep to apply to all three, and on this one it would be a regression.

      The settings `narrow` flag caps forms and prose and leaves data tables and the permissions
      matrix wide, "because they use the width productively and would be actively worse boxed
      in". This page is both of the uncapped kinds and neither of the capped ones:

        - LEFT is a members data table (`fillParent`, full height).
        - RIGHT is a FIXED 452px rail. It does not stretch with the window at all, so the
          label→control spread that made UserPage worth capping cannot happen here — measured
          267px between a permission label and its switch at BOTH 1280 and 1920, unchanged.

      So every extra pixel goes to the table, and the table uses them. Measured at 1920 (content
      area 1840), actual vs a simulated 1100 cap under identical conditions:

        members grid          1316px → 624px          (= 1100 − 452 rail − 24 gap)
        horizontal overflow   0px    → 692px

      i.e. capping does not make the table narrower and tidier, it pushes the email and actions
      columns out of view behind a horizontal scrollbar. That is not hypothetical: at 1280 with
      the sidebar expanded the grid is already down to 500px and the email column is cut
      mid-address ("alice.admin@louisvu…"). A cap would make every wide window behave like that
      worst case and spend the reclaimed width on empty gutters.

      Capping would also fight the layout: `height: vhMinus(48)` makes this a viewport-filling
      view, and a full-height table centred in a 1100px column inside a 1664px content area is
      not what a centred readable measure is for.

      Fill the page content area (viewport minus the 24px top+bottom gutters) and let the
      two-column row flex to the bottom — pure CSS, no height magic.
    */
    <Stack spacing={6} sx={{ height: vhMinus(48) }}>
      {/* ── Header with breadcrumb + status chip + action buttons ── */}
      <Header
        title={displayName}
        breadcrumbsOveride={displayName}
        status={{ statuses: ROLE_STATUSES, value: getRoleStatus(role) }}
        rightComponents={
          // Action group order (header-action-hierarchy convention §26): utility
          // secondaries first, then the destructive (Delete) immediately left of
          // the PRIMARY rightmost. "Assign users" is the role page's main action.
          // Delete is `destructive="hover"` (neutral until hovered) + confirm
          // modal, so its adjacency to the primary isn't a mis-click trap.
          <Stack direction="row" spacing={2} alignItems="center">
            <Button
              variant="secondary"
              size="medium"
              startIcon={<CopyIcon size={16} strokeWidth={1.5} />}
              onClick={() => setOpenDuplicateModal(true)}
            >
              Duplicate
            </Button>
            {role.isCustom && (
              <Button
                variant="secondary"
                size="medium"
                destructive="hover"
                startIcon={<Trash2Icon size={16} strokeWidth={1.5} />}
                onClick={() => setOpenDeleteModal(true)}
              >
                Delete
              </Button>
            )}
            {/* §27: stays visible + disabled with a tooltip naming the missing permission,
                rather than hidden — a disabled MUI Button swallows pointer events, so the
                Tooltip needs a span wrapper to still receive them. */}
            <Tooltip
              title={canManageMembers ? '' : "You need the 'Assign users' permission. Ask an admin to grant it."}
              arrow
            >
              <span style={{ display: 'inline-flex' }}>
                <Button
                  variant="primary"
                  size="medium"
                  disabled={!canManageMembers}
                  startIcon={<UserPlusIcon size={16} strokeWidth={1.5} />}
                  onClick={() => setOpenAssignModal(true)}
                  data-tour="role-assign-users"
                >
                  Assign users
                </Button>
              </span>
            </Tooltip>
          </Stack>
        }
      />

      {/* ── Two-column body — flex:1 fills the rest of the page down to the
          bottom gutter; `stretch` makes both columns fill that height so their
          bottoms land exactly at the gutter. ── */}
      <Stack direction="row" spacing={6} alignItems="stretch" sx={{ flex: 1, minHeight: 0 }}>

        {/* ── LEFT: Members table — fills the column via CSS flex (fillParent),
            no height magic. overflowX clips horizontal overflow only. ── */}
        <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, overflowX: 'hidden' }}>
          <Table
            fillParent
            rows={members}
            columns={memberColumns}
            showSelection={false}
            loading={isMembersLoading}
            getRowId={(row) => row.userId}
            emptyState={
              <EmptyState
                title="No members"
                text="No users have this role yet."
                icon={<UsersIcon strokeWidth={1} size={32} />}
              />
            }
          />
        </Box>

        {/* ── RIGHT: Details + Permissions stacked cards — fills the row height
            (stretch); the Permissions card flexes and scrolls internally. ── */}
        <Stack sx={{ width: 452, flexShrink: 0, height: '100%', minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }} spacing={3}>

          {/* Details card */}
          <Card title="Details" sx={{ flexShrink: 0 }}>
            <Stack spacing={3}>
              {isEditingName ? (
                /* Edit state */
                <Stack spacing={3}>
                  <Input
                    label="Role name"
                    value={nameValue}
                    onChange={(v: string) => setNameValue(v)}
                    autoFocus
                  />
                  <Stack direction="row" spacing={2}>
                    <Button
                      variant="secondary"
                      startIcon={<XIcon size={14} strokeWidth={1.5} />}
                      onClick={handleCancelEdit}
                      disabled={renameRole.isPending}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      startIcon={<CheckIcon size={14} strokeWidth={1.5} />}
                      onClick={handleSaveName}
                      disabled={!nameValue.trim() || renameRole.isPending}
                    >
                      Save
                    </Button>
                  </Stack>
                </Stack>
              ) : (
                /* View state — labelled "Role name" (body3 / 8px gap, matching
                   the Input's field label) so the value reads as a field. */
                <Stack spacing={2}>
                  <Typography variant="body3" color="text.secondary">
                    Role name
                  </Typography>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={2}
                  >
                    <Typography variant="body2">{displayName}</Typography>
                    {role.isCustom && (
                      <Button
                        variant="secondary"
                        startIcon={<PencilIcon size={14} strokeWidth={1.5} />}
                        onClick={handleEditName}
                      >
                        Edit
                      </Button>
                    )}
                  </Stack>
                </Stack>
              )}
            </Stack>
          </Card>

          {/* Permissions card — `fill` makes its inner wrappers flex:1/minHeight:0
              so the PermissionsList scrolls inside the card instead of overflowing. */}
          <Card fill sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <PermissionsList roleId={role.id} />
          </Card>
        </Stack>
      </Stack>

      {/* ── Modals ── */}
      <AssignUsersModal
        role={roleForModals}
        open={openAssignModal}
        onClose={() => setOpenAssignModal(false)}
        onAssigned={() => refetchMembers()}
        existingMemberIds={existingMemberIds}
      />
      <DeleteRoleModal
        role={roleForModals}
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        onDeleted={handleDeleted}
      />
      <DuplicateRoleModal
        role={roleForModals}
        open={openDuplicateModal}
        onClose={() => setOpenDuplicateModal(false)}
        // Navigate to the freshly-created duplicate's own page.
        onDuplicated={(newRole) =>
          navigate(ROUTES.SETTINGS_COMPANY_ROLE(newRole.name))
        }
      />
    </Stack>
  )
}
